import authService from './authService';

export interface Batch {
  batch_id: string;
  batch_name: string;
  del_row?: boolean;
  max_no_of_students: number;
  students_count: number;
}

/**
 * Fetch all batches for a specific course (plural endpoint)
 * @param courseId - The course ID to fetch batches for
 * @returns Promise<Batch[]> Array of batch objects
 */
export const fetchBatchesByCourse = async (courseId: string): Promise<Batch[]> => {
  try {
    const response = await authService.get(`admin/batch/get_all_batches/${courseId}/`);
    
    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to fetch batches');
    }
    
    return response.data.batches || [];
  } catch (error) {
    console.error('Error fetching batches:', error);
    throw error;
  }
};

/**
 * Fetch all batches for a specific course (singular endpoint)
 * @param courseId - The course ID to fetch batches for
 * @returns Promise<Batch[]> Array of batch objects
 */
export const fetchBatchesByCourseSingle = async (courseId: string): Promise<Batch[]> => {
  const normalizedCourseId = String(courseId || '').trim();
  if (!normalizedCourseId) return [];

  try {
    const response = await authService.get(
      `admin/batch/get_all_batch/${encodeURIComponent(normalizedCourseId)}/`,
    );

    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to fetch batches');
    }

    const raw = response?.data?.batches ?? response?.data ?? [];
    const list = Array.isArray(raw) ? raw : [];
    return list
      .map((batch: Partial<Batch> & { students_count?: number; student_count?: number }) => ({
        batch_id: String(batch.batch_id || '').trim(),
        batch_name: String(batch.batch_name || '').trim(),
        del_row: Boolean(batch.del_row),
        max_no_of_students: Number(batch.max_no_of_students) || 0,
        students_count: Number(batch.students_count ?? batch.student_count) || 0,
      }))
      .filter((batch) => batch.batch_id && batch.batch_name);
  } catch (error) {
    console.error('Error fetching batches:', error);
    throw error;
  }
};

/**
 * Fetch all batches for multiple courses at once
 * @param courseIds - Array of course IDs
 * @returns Promise<Batch[]> Array of batch objects
 */
export const fetchBatchesByCoursesMulti = async (courseIds: string[]): Promise<Batch[]> => {
  if (courseIds.length === 0) return [];
  try {
    const joined = courseIds.join(',');
    const response = await authService.get(`admin/batch/get_all_batch/${joined}/`);
    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to fetch batches for multiple courses');
    }
    return response.data.batches || [];
  } catch (error) {
    console.error('Error fetching batches for multiple courses:', error);
    throw error;
  }
};

